import { useState } from 'react';

import successIcon from '@/assets/images/success-icon.webp';
import warningIcon from '@/assets/images/warning-icon.webp';
import { ResultDialog } from '@/components/feedback/result-dialog';
import type { Device } from '@/features/devices';

import { WarrantyClaimModal } from './warranty-claim-modal';

export type WarrantyClaimFlowProps = {
  open: boolean;
  /** Devices the grower can claim against. */
  devices: Device[];
  /** Lock the selector to one device (device-detail entry); omit for the listing entry. */
  lockedDeviceId?: string;
  onClose: () => void;
};

type Override = 'success' | 'failed' | null;

/**
 * Orchestrates the Warranty Claim experience from either the device-detail context (device
 * locked) or the Warranty Claims listing (device selectable):
 *
 *   open → form → success (Figma 2389-752622) | failure
 *
 * Eligibility (within-warranty / no open claim) is enforced upstream on the device-detail
 * action and re-checked server-side at submit; a failed check routes here to the failure
 * dialog.
 */
export function WarrantyClaimFlow({
  open,
  devices,
  lockedDeviceId,
  onClose,
}: WarrantyClaimFlowProps) {
  const [override, setOverride] = useState<Override>(null);

  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setOverride(null);
  }

  const showForm = open && override === null;

  return (
    <>
      <WarrantyClaimModal
        open={showForm}
        devices={devices}
        lockedDeviceId={lockedDeviceId}
        onClose={onClose}
        onSubmitted={() => setOverride('success')}
        onFailed={() => setOverride('failed')}
      />

      <ResultDialog
        open={open && override === 'success'}
        icon={successIcon}
        title="Success"
        subtitle="Your warranty claim has been submitted successfully. Our team will get back to you soon."
        onClose={onClose}
      />

      <ResultDialog
        open={open && override === 'failed'}
        icon={warningIcon}
        title="Warranty claim not submitted"
        subtitle="Please review the information provided and try again. If the issue continues, contact support for assistance."
        onClose={onClose}
      />
    </>
  );
}
